import React from 'react';
import { observer } from 'src/utils/mobx-react';
import {
    SrgVerdictParagraph,
    StaticLinkWrapper,
    SrgParagraph,
    SrgQuestionnaireButton,
    SrgQuestionnaireEmailOrPhoneLink,
} from 'src/domains/players/state/srgQuestionnaire/SrgQuestionnaire.style';

import { LanguageTokenType } from 'src/domains/layouts/state/languagesState/LanguagesUtils';
import { SrgVerdictFailedPrimaryPropsType } from 'src/domains/players/state/srgQuestionnaire/SrgVerdict';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';

export const SrgVerdictFailedPrimary = observer(
    'SrgVerdictFailedPrimary',
    ({ operatorFullName, languagesState, handleCloseModal }: SrgVerdictFailedPrimaryPropsType): JSX.Element | null => {
        const HelpLineString = observer(
            'GamstopInformation',
            ({ languagesState }: { languagesState: LanguagesState }) => {
                const greetingString = languagesState.translateTokens(
                    languagesState.getTranslation(
                        'srg.verdict.fail.part4',
                        'Its confidential helpline is: [helpLine]. Non-UK residents can contact GamCare for details of International support organisations.'
                    ),

                    (singleParam: LanguageTokenType) => {
                        if (singleParam.tag === 'helpLine') {
                            return (
                                <SrgQuestionnaireEmailOrPhoneLink
                                    href='tel:0845 6000 133'
                                    isHighlited={true}
                                >
                                    0845 6000 133
                                </SrgQuestionnaireEmailOrPhoneLink>
                            );
                        }
                    }
                );

                return <SrgParagraph>{greetingString}</SrgParagraph>;
            }
        );

        return (
            <SrgVerdictParagraph data-test='srg-verdict-paragraph'>
                <SrgParagraph>
                    <I18n
                        langKey='srg.verdict.fail.part1'
                        defaultText='{operatorFullName} is committed to responsible gambling as a policy of customer care and social responsibility. Therefore, following your answers to our responsible gambling interaction, we have decided to restrict you from placing further deposits or bets on your account. Instead, you have 7 days to cash out any open bets and withdraw any available funds. Once this time has elapsed, your account will then be closed.'
                        params={{ operatorFullName }}
                    />
                </SrgParagraph>

                <SrgParagraph>
                    <I18n
                        langKey='srg.verdict.fail.part2'
                        defaultText='We would also like to make you aware of the below organisations available to you:'
                    />
                </SrgParagraph>

                <SrgParagraph>
                    <I18n
                        langKey='srg.verdict.fail.part3'
                        defaultText='GamCare: The leading authority providing counselling, advice, and practical help in addressing the social impact of gambling in the UK, can be visited at '
                    />
                    <StaticLinkWrapper
                        isUnderlined={true}
                        to='https://www.gamcare.org.uk'
                    >
                        www.gamcare.org.uk
                    </StaticLinkWrapper>
                </SrgParagraph>

                <SrgParagraph>
                    <HelpLineString languagesState={languagesState} />
                </SrgParagraph>

                <SrgParagraph>
                    <I18n
                        langKey='srg.verdict.fail.part5'
                        defaultText='Gamblers Anonymous: A fellowship of men and women who have joined together to do something about their gambling problem and help other compulsive gamblers do the same. There are regional fellowships around the world.'
                    />
                </SrgParagraph>

                <SrgParagraph>
                    <I18n
                        langKey='srg.verdict.fail.part6'
                        defaultText='The Gamblers Anonymous international service site is at '
                    />
                    <StaticLinkWrapper
                        isUnderlined={true}
                        to='https://www.gamblersanonymous.org.uk'
                    >
                        www.gamblersanonymous.org.uk
                    </StaticLinkWrapper>
                </SrgParagraph>

                <SrgParagraph>
                    <I18n
                        langKey='srg.verdict.fail.part7'
                        defaultText='Gamstop: Gamstop lets you put controls in place to restrict your online gambling activities. You will be prevented from using gambling websites and apps run by companies licensed in Great Britain, for a period of your choosing: '
                    />
                    <StaticLinkWrapper
                        isUnderlined={true}
                        to='https://www.gamstop.co.uk'
                    >
                        www.gamstop.co.uk
                    </StaticLinkWrapper>
                </SrgParagraph>

                <SrgQuestionnaireButton
                    size='large'
                    onClick={handleCloseModal}
                    dataTest='close-button'
                >
                    Ok, I understand
                </SrgQuestionnaireButton>
            </SrgVerdictParagraph>
        );
    }
);
